/**
 * topic_dialogue.ts — PURE Morrowind-style topic dialogue for versonas (the
 * no-LLM fallback brain). Three mechanics transplanted from TES III:
 *
 *   · TOPICS — a shared topic database; an NPC "knows" a topic when at least
 *     one of its rules passes their facts (job/civ/war gates). The window
 *     lists known base topics ∪ whatever the player has DISCOVERED.
 *   · HYPERLINKS — responses are scanned for other known topics' keywords;
 *     matches render as clickable links that add the topic to the list
 *     (discovery), exactly like Morrowind's blue keywords.
 *   · GREETINGS — a priority ladder (crisis > strong feeling > standing
 *     extremes > recognized-returning > activity/place > generic), mirroring
 *     Greeting 0..9 "most restrictive first" doctrine.
 *
 * Awareness comes free from the replicated sim: what they're DOING
 * (versona_state activity vocabulary), WHERE they are (nearest named town
 * slot), rumors they personally carry (versona_memory kind='rumor'), the war,
 * offices, prices, and how the town regards the PLAYER (player_standing —
 * regard IS Morrowind disposition, server-computed).
 *
 * Free-text NLP (matchTopic): normalize → stopword-strip → light stem →
 * alias phrase/token scoring with typo tolerance (Levenshtein ≤2 on long
 * tokens) — "any rumers goin around?" still lands on Latest rumors.
 */

import { LORE_PLACES, LORE_SPACE, LORE_TECH, LORE_NATURE, type LoreEntry } from './topic_lore.js';
import { LORE_B } from './topic_lore2.js';
import { LORE_C } from './topic_lore3.js';

// ─── Facts ───────────────────────────────────────────────────────────────────

export interface TopicFacts {
  // who they are
  name: string;
  job: string;
  /** versona_state activity: working|trading|to_market|to_work|to_social|social|chatting|resting|patrolling|wandering|building|idle */
  state: string;
  civName: string;      // 'Aurelian Concord' | 'Borean Compact' | ''
  civDemonym: string;   // 'Aurelian' | 'Borean' | ''
  rivalDemonym: string; // the other one ('' when civ unknown)
  workplace: string;    // 'the Borean ore works' ('' unknown)
  // where they are
  town: string;         // settlement display name ('' unknown)
  venue: string;        // nearest named town slot ('' none close)
  venueFn: string;      // its function key (market|entertainment|faith|…)
  // sky
  tod: 'morning' | 'day' | 'evening' | 'night';
  weather: string;      // 'clear' | 'rain' | 'snow' | '' …
  // inner life
  hungry: boolean;
  happy: boolean;
  grumpy: boolean;
  feelingTone: '' | 'angry' | 'glad' | 'uneasy' | 'down';
  feelingCause: string;
  memoryLine: string;
  rumors: string[];     // hearsay THIS NPC carries (versona_memory kind='rumor')
  townNews: string;     // newest npc_event detail ('' quiet)
  familyLine: string;   // 'the Galridge household' ('' unknown)
  // town state
  foodPrice: number;
  taxPct: number;
  project: string;
  custom: string;       // newest active settlement norm
  mayorName: string;
  isMayor: boolean;
  keeperName: string;      // settlement_office 'keeper'
  guildmasterName: string; // settlement_office 'guildmaster'
  // war
  atWar: boolean;
  warFoe: string;       // the enemy town's name ('' peace)
  warCasualties: number;
  warWeary: boolean;    // exhaustion high on their side
  // the player, as this town sees them
  playerName: string;
  standing: number;     // player_standing regard 0..100 (lazy-decayed server-side)
  chattedBefore: boolean;
  isResident: boolean;
  // relationship wave — the personal ladder (never a number on screen)
  relTier: 'nemesis' | 'hostile' | 'cold' | 'stranger' | 'familiar' | 'friend' | 'confidant';
  relNet: number;         // the decayed event-stack net (drives behavior only)
  seenCount: number;      // mere-exposure ticks
  talkStreak: number;
  rivalFlag: boolean;
  /** Top ± relation events, first-person citable ("%why1%"). */
  relWhys: Array<{ label: string; weight: number; ago: string }>;
  /** DISPOSITION (2026-09-15): the four axes behind the tier — fear keeps them wary whatever the net says. */
  disposition?: { warmth: number; trust: number; fear: number; obligation: number };
  aspirationLine: string; // their life arc, first person ('' unknown)
  // cognition wave — the semantic layer, surfaced (features 24/29/26)
  thought: string;        // versona_thought inner voice ('' none)
  diaryLine: string;      // latest 'reflection' belief ('' none)
  bondLine: string;       // warmest 'bond' belief ('' none)
  witnessLines: string[]; // top witness memories (retrieval-lite ranked)
  playerOpinion: number;  // THIS versona's personal 'player' belief weight (0 = none)
}

export type StandingTier = 'wary' | 'stranger' | 'familiar' | 'neighbor';

/** regard → the four Morrowind-disposition-like tones the lines key off. */
export function standingTierOf(regard: number): StandingTier {
  if (regard < 15) return 'wary';
  if (regard < 40) return 'stranger';
  if (regard < 70) return 'familiar';
  return 'neighbor';
}

// ─── Topic database ──────────────────────────────────────────────────────────

type Criterion = (f: TopicFacts) => boolean;
interface TopicRule { criteria: Criterion[]; lines: string[] }

export interface TopicDef {
  key: string;
  label: string;
  /** Match/link vocabulary — first alias is the "natural" word hyperlinks use. */
  aliases: string[];
  /** Listed without discovery (when known)? Otherwise it must be found via links or typing. */
  base: boolean;
  /** Does THIS NPC know the topic at all (Morrowind's "has a passing line"). */
  knows: Criterion;
  /** Relationship information-gate: below this regard the NPC deflects the
   *  topic instead of answering (the deflection names the cure: trust). */
  minStanding?: number;
  rules: TopicRule[];
  /** Relationship-tier floor (disclosure ladder) — checked beside minStanding. */
  minTier?: 'familiar' | 'friend' | 'confidant';
}

const is = <K extends keyof TopicFacts>(k: K, v: TopicFacts[K]) => (f: TopicFacts): boolean => f[k] === v;
const tier = (t: StandingTier) => (f: TopicFacts): boolean => standingTierOf(f.standing) === t;
const tierAtLeast = (min: 'familiar' | 'neighbor') => (f: TopicFacts): boolean => {
  const t = standingTierOf(f.standing);
  return min === 'familiar' ? t === 'familiar' || t === 'neighbor' : t === 'neighbor';
};
const always: Criterion = () => true;
const BUSY_STATES = new Set(['working', 'building', 'trading']);

export const TOPICS: TopicDef[] = [
  {
    key: 'rumors', label: 'Latest word', base: true,
    aliases: ['rumors', 'rumor', 'gossip', 'news', 'word', 'stories', 'the wire', 'bulletins', 'hear anything'],
    knows: always,
    rules: [
      { criteria: [], lines: ['Quiet on the wire lately, and I don\'t mind it. When the haulers start talking, so do we.', 'Nothing worth repeating. Ask after the next land-train docks.'] },
      { criteria: [(f) => f.townNews !== ''], lines: ['Word around the settlement: %news%.', 'You didn\'t hear it from me — %news%.'] },
      { criteria: [(f) => f.rumors.length > 0], lines: ['%rumor%', 'Keep it off the net, but — %rumor%', 'The roads carry stories in with the freight. %rumor%'] },
      { criteria: [(f) => f.rumors.length > 0, is('grumpy', true)], lines: ['People talk too much in this settlement. Fine: %rumor%'] },
      { criteria: [(f) => f.atWar], lines: ['All anyone talks about is the war with %foe%. The rest is static.'] },
    ],
  },
  {
    key: 'memories', label: 'What they remember', base: true,
    aliases: ['remember', 'memories', 'memory', 'seen anything', 'witnessed', 'what have you seen'],
    knows: () => true,
    rules: [
      { criteria: [(f) => (f.witnessLines?.length ?? 0) > 0], lines: ['I remember it plain: %witness%', 'You want what these eyes have kept? %witness%', '%witness% — that one stays with me.'] },
      { criteria: [], lines: ['Quiet days, mostly. The kind you forget on purpose.', 'Nothing worth keeping lately — and that suits me.'] },
    ],
  },
  {
    key: 'mind', label: 'On their mind', base: true,
    aliases: ['thinking', 'your mind', 'on your mind', 'thoughts', 'what are you thinking', 'penny for'],
    knows: () => true,
    rules: [
      { criteria: [(f) => f.diaryLine !== '' && f.standing >= 40], lines: ['Between us? Last night I set it down like this: %diary%.', 'My own reckoning of it: %diary%. Keep that to yourself.'] },
      { criteria: [(f) => f.thought !== ''], lines: ['Right now? %thought%', 'Honestly — %thought%'] },
      { criteria: [], lines: ['One foot in front of the other. That is the whole of it.'] },
    ],
  },
  {
    key: 'us', label: 'About the two of you', base: true,
    aliases: ['about me', 'think of me', 'what do you think of me', 'us', 'our history', 'know me'],
    knows: () => true,
    rules: [
      { criteria: [(f) => f.relTier === 'nemesis'], lines: ['You want it plain? %why1%. I will not forget, and I will not let this town forget.'] },
      { criteria: [(f) => f.relTier === 'hostile' || f.relTier === 'cold'], lines: ['Little good. %why1%.', 'You want honesty? %why1%. Make of that what you will.'] },
      { criteria: [(f) => (f.relWhys?.length ?? 0) >= 2], lines: ['Where to start. %why1%. And %why2%.', 'I keep accounts, friend: %why1%. Also %why2%.'] },
      { criteria: [(f) => (f.relWhys?.length ?? 0) >= 1], lines: ['Truthfully? %why1%. That is most of what I know of you.'] },
      { criteria: [], lines: ['We barely know each other — give it time, or a kindness.'] },
    ],
  },
  {
    key: 'dreams', label: 'Their dream', base: false, minTier: 'friend',
    aliases: ['dream', 'dreams', 'hope for', 'ambition', 'what do you want', 'aspiration'],
    knows: () => true,
    rules: [
      { criteria: [(f) => f.aspirationLine !== ''], lines: ['Between friends? %aspiration%. Laugh and I will deny it.', 'I have never told the street this: %aspiration%.'] },
      { criteria: [], lines: ['Dreams are for people with lighter workloads. Ask me again someday.'] },
    ],
  },
  {
    key: 'work', label: 'Their trade', base: true,
    aliases: ['work', 'trade', 'job', 'craft', 'living', 'labor', 'shift', 'what do you do'],
    knows: always,
    rules: [
      { criteria: [], lines: ['%job% work, mostly. It keeps me fed and the settlement standing.', 'I\'m a %job%. Not glamorous, but the town runs on it.'] },
      { criteria: [is('job', 'farmer')], lines: ['I work the growing terraces. Rations post at %price% at the exchange — you can read a whole season in that number.', 'Crops don\'t tend themselves, heat lines or no. The terraces feed this settlement.'] },
      { criteria: [is('job', 'miner')], lines: ['Ore. Down the rigs at first light, up with a full cart if my back holds.', 'The seams run deep under the ridge. Someone has to follow them down.'] },
      { criteria: [is('job', 'smith')], lines: ['I take the rig crews\' ore and give the town back tools. The fabrication halls never fully cool.', 'Fabricator work at %workplace%. No ore, no parts; no parts, no town.'] },
      { criteria: [is('job', 'merchant')], lines: ['I buy, I sell, I listen. A market stall hears more than a sensor mast ever will.', 'Trade. Spreads, freight boards, and knowing which rumors move credits.'] },
      { criteria: [is('job', 'guard')], lines: ['I hold the watch. Mostly nothing comes over the wall — mostly.', 'Patrols and gate duty. The quiet is the deliverable.'] },
      { criteria: [is('job', 'bard')], lines: ['Songs are goods too, friend. I play the canteen evenings — come by.', 'I trade in stories and song chips. The canteen pays better than you\'d think.'] },
      { criteria: [is('job', 'scout')], lines: ['I walk the far ground so the rest don\'t have to. The wilds aren\'t empty, whatever the survey summaries say.', 'Survey work. Past the ridge and back, and the map\'s still never done.'] },
      { criteria: [(f) => BUSY_STATES.has(f.state)], lines: ['As you can see — mid-shift. But talk, I can work and listen.'] },
      { criteria: [is('hungry', true)], lines: ['Hard to do good work on an empty stomach, I\'ll tell you that much.'] },
    ],
  },
  {
    key: 'town', label: 'This settlement', base: true,
    aliases: ['town', 'settlement', 'city', 'place', 'colony', 'living here'],
    knows: always,
    rules: [
      { criteria: [], lines: ['%town%. Small on the chart, but it\'s ours. The market sits at the center — rations run %price% these days.', 'You\'re standing in %town%. Mind the customs and keep to the lamps after dark, and you\'ll do fine.'] },
      { criteria: [(f) => f.project !== ''], lines: ['The settlement\'s building %project% — treasury credits at work for once. Opinions vary.', 'They broke ground on %project%. About time, if you ask me.'] },
      { criteria: [(f) => f.foodPrice > 8], lines: ['Rations are dear — %price% a crate. The terraces can\'t keep up. Bring grain in and you\'ll make friends fast.'] },
      { criteria: [(f) => f.foodPrice < 3], lines: ['The terraces have been kind. Rations at %price% — cheap as scrap. Buyer\'s market.'] },
      { criteria: [(f) => f.custom !== ''], lines: ['Way we do things here: %custom%', 'New folk learn quick — %custom%'] },
      { criteria: [(f) => f.atWar], lines: ['A settlement at war isn\'t itself. %town% will remember these months, one way or the other.'] },
      { criteria: [is('isMayor', true)], lines: ['Ask the one who keeps it. This settlement is my charge, and I know every bolt of it.'] },
    ],
  },
  {
    key: 'here', label: 'This spot', base: true,
    aliases: ['this spot', 'where are we', 'this building', 'what is this place', 'right here'],
    knows: always,
    rules: [
      { criteria: [], lines: ['Just a stretch of %town%. The named halls are back toward the center.', 'Open ground. Good for walking, bad for shade.'] },
      { criteria: [(f) => f.venue !== ''], lines: ['We\'re by %venue%. %venuewhy%', 'This? %venue%. %venuewhy%'] },
      { criteria: [(f) => f.venue !== '', is('venueFn', 'entertainment')], lines: ['%venue%. Half the settlement\'s stories start in here, and most of the rumors.'] },
      { criteria: [(f) => f.venue !== '', is('venueFn', 'market')], lines: ['%venue% — the settlement\'s pulse. Watch the spreads and you\'ll know our fortunes before the keeper does.'] },
      { criteria: [(f) => f.venue !== '', is('venueFn', 'faith')], lines: ['%venue%. Keep your voice low; some come here to say names.'] },
    ],
  },
  {
    key: 'me', label: 'Yourself', base: true,
    aliases: ['me', 'myself', 'yourself', 'about me', 'think of me', 'my name'],
    knows: always,
    rules: [
      { criteria: [], lines: ['%player%, isn\'t it? Word gets around a settlement this size.', 'I know your name, %player%. Out here that\'s a start.'] },
      { criteria: [tier('wary')], lines: ['I know who you are, %player%, and the word isn\'t kind. Earn some trust and we\'ll speak properly.', 'You\'re watched here, %player%. Nothing personal — the settlement decides these things.'] },
      { criteria: [tier('stranger')], lines: ['An off-worlder to us still, %player%. Trade honestly, lend a hand — towns notice.', 'Not much yet, truth told. Give %town% a reason to remember your name kindly.'] },
      { criteria: [tier('familiar')], lines: ['A familiar face now, %player%. People nod when you pass — that\'s worth more than credits here.'] },
      { criteria: [tier('neighbor')], lines: ['You\'re well-regarded in %town%, %player% — a neighbor in all but roof. It shows.', 'People speak warmly of you, %player%. Don\'t waste that; it\'s slow to earn twice.'] },
      { criteria: [is('isResident', true), (f) => f.standing >= 0], lines: ['You hold a residence here — that makes you one of ours, %player%, charter and all.'] },
      { criteria: [is('chattedBefore', true), tier('familiar')], lines: ['We\'ve talked before, you and I. I remember. A %job% remembers faces.'] },
    ],
  },
  {
    key: 'people', label: 'Their people', base: true,
    aliases: ['people', 'aurelian', 'borean', 'concord', 'compact', 'civilization', 'your folk', 'kin'],
    knows: (f) => f.civDemonym !== '',
    rules: [
      { criteria: [], lines: ['We\'re %civ% here. You\'ll know it by the build of the place.'] },
      { criteria: [is('civDemonym', 'Aurelian')], lines: ['The Concord keeps its word — that\'s the whole creed. Spires up, ledgers public.', 'Aurelian, born and raised. We build toward the light; the %rival%s dig away from it. Different answers to the same winters.'] },
      { criteria: [is('civDemonym', 'Borean')], lines: ['The Compact provides. Boreans don\'t promise — we stock the vaults and seal the doors on the cold.', 'Borean, me. Stone, steam, and no wasted words. The %rival%s can keep their glitter.'] },
      { criteria: [(f) => f.atWar], lines: ['My people are at war. Whatever I think of the %rival%s, I\'d not wish these months on their households either.'] },
    ],
  },
  {
    key: 'rivals', label: 'The other folk', base: false,
    aliases: ['rivals', 'other folk', 'them', 'the other side', 'outsiders'],
    knows: (f) => f.rivalDemonym !== '',
    rules: [
      { criteria: [], lines: ['The %rival%s? Different creed, same weather. We trade more than either council admits.', 'Honest credits spend the same in both ledgers. That\'s all I\'ll say about the %rival%s.'] },
      { criteria: [(f) => f.atWar], lines: ['Right now the %rival%s are the war, and the war is %foe%. Ask me again in kinder months.'] },
      { criteria: [is('grumpy', true)], lines: ['Don\'t get me started on the %rival%s. Not today.'] },
    ],
  },
  {
    key: 'war', label: 'The war', base: true,
    aliases: ['war', 'fighting', 'conflict', 'battle', 'clash'],
    knows: (f) => f.atWar,
    rules: [
      { criteria: [], lines: ['War with %foe%. Grievance filed on grievance until the councils stopped answering.', 'It\'s real, whatever the bulletins dress it in. %foe% and us, and no quick end on the board.'] },
      { criteria: [(f) => f.warCasualties > 0], lines: ['We\'ve said the names three times for %casualties% already. Every empty chair is a number to the council and a name to a table.'] },
      { criteria: [is('warWeary', true)], lines: ['The settlement is tired of it. You can feel it at the exchange — everyone counting what the war costs and calling it something else.'] },
      { criteria: [is('job', 'guard')], lines: ['The watch doubles when there\'s war. I sleep in my boots and count the road cairns twice.'] },
    ],
  },
  {
    key: 'keeper', label: 'The keeper', base: false,
    aliases: ['keeper', 'mayor', 'council', 'office', 'who leads', 'in charge', 'guildmaster'],
    knows: (f) => f.mayorName !== '' || f.keeperName !== '' || f.guildmasterName !== '',
    rules: [
      { criteria: [], lines: ['%mayor% keeps the settlement. Fair enough at it, as keepers go.', 'That would be %mayor%. Take your grievances there, not to me.'] },
      { criteria: [(f) => f.guildmasterName !== ''], lines: ['%mayor% keeps the town; %guildmaster% speaks for the guilds. Between them most matters get settled.'] },
      { criteria: [(f) => f.taxPct >= 8], lines: ['%mayor% keeps the town — and keeps the levy at %tax%, which is the popular complaint. Ask about the levy if you want an earful.'] },
      { criteria: [is('isMayor', true)], lines: ['You\'re speaking to them. I keep %town% — the ledgers, the works, and the peace, in that order some days.'] },
    ],
  },
  {
    key: 'taxes', label: 'The levy', base: false,
    aliases: ['taxes', 'tax', 'levy', 'tithe', 'dues'],
    knows: always,
    rules: [
      { criteria: [], lines: ['The levy sits at %tax%. It pays for the works and the watch, they tell us.', 'Levy runs %tax% here. Could be worse; has been worse.'] },
      { criteria: [(f) => f.taxPct >= 8], lines: ['%tax%, and heavier every market day it feels. Somebody\'s fountain had better be worth it.'] },
      { criteria: [(f) => f.taxPct >= 8, is('grumpy', true)], lines: ['%tax%! Lane piracy with a charter stamp on it.'] },
      { criteria: [(f) => f.taxPct < 4], lines: ['Light, mercifully — %tax%. A settlement that taxes light and eats well has little to riot over.'] },
      { criteria: [is('isMayor', true)], lines: ['I set it at %tax%, and I\'ll defend every point of it. Reactors and walls don\'t pay for themselves.'] },
    ],
  },
  {
    key: 'market', label: 'Trade & prices', base: false,
    aliases: ['market', 'prices', 'haulers', 'goods', 'buy', 'sell', 'grain', 'food', 'rations', 'caravans', 'harvest'],
    knows: always,
    rules: [
      { criteria: [], lines: ['Rations run %price% at the exchange. Everything else keys off that, whatever the traders pretend.', 'Watch the freight board. When the land-trains stop coming, prices talk louder than the crier.'] },
      { criteria: [(f) => f.foodPrice > 8], lines: ['Dear. %price% for a crate that was half that in a good season. The terraces are behind and everyone knows it.'] },
      { criteria: [(f) => f.foodPrice < 3], lines: ['%price% a crate — a glut. The growers grumble; everyone else eats twice.'] },
      { criteria: [is('job', 'merchant')], lines: ['Now you\'re speaking my language. Rations at %price%, and the margin lives in knowing which settlement is hungrier — the haulers carry more than crates, friend.'] },
      { criteria: [(f) => f.atWar], lines: ['War prices. Freight thins, escorts cost, and every crate carries a little fear in with the goods.'] },
    ],
  },
  {
    key: 'tavern', label: 'The canteen', base: false,
    aliases: ['tavern', 'canteen', 'drink', 'ale', 'still-brew', 'inn', 'songs', 'evenings'],
    knows: always,
    rules: [
      { criteria: [], lines: ['Evenings, the canteen\'s where the settlement breathes out. Songs, still-brew, and rumors at a fair exchange rate.', 'Best place to learn a settlement is its canteen, an hour past dusk.'] },
      { criteria: [is('job', 'bard')], lines: ['My stage, that. Come by after dusk — first song\'s free, the gossip costs a drink.'] },
      { criteria: [is('tod', 'evening')], lines: ['This hour? Half the town\'s already there. Follow the noise.'] },
      { criteria: [is('job', 'guard')], lines: ['Where trouble goes to be born, and the watch goes to meet it. Good brew though.'] },
    ],
  },
  {
    key: 'family', label: 'Their household', base: false,
    aliases: ['family', 'household', 'children', 'partner', 'parents', 'married'],
    knows: always,
    rules: [
      { criteria: [], lines: ['Family\'s a private ledger, friend. But everyone here belongs to someone.'] },
      { criteria: [(f) => f.familyLine !== ''], lines: ['I\'m of %family%. In a settlement this size, the surname does half the introducing.', '%family%, for my sins and my suppers.'] },
      { criteria: [(f) => f.familyLine !== '', is('happy', true)], lines: ['%family% — and proud of it. We look after our own.'] },
    ],
  },
  {
    key: 'feelings', label: 'How they fare', base: false,
    aliases: ['feelings', 'how are you', 'fare', 'doing', 'alright', 'mood', 'upset', 'wrong', 'bothering'],
    knows: always,
    rules: [
      { criteria: [], lines: ['Fed, roofed, and busy. That\'s as much as anyone out here asks.', 'Well enough, thanks for asking. Few do.'] },
      { criteria: [is('feelingTone', 'angry')], lines: ['Honestly? Sore about it: %cause%. Don\'t take the short answers personally.'] },
      { criteria: [is('feelingTone', 'glad')], lines: ['Good, truly. %cause% — that\'s put the wind in me.'] },
      { criteria: [is('feelingTone', 'uneasy')], lines: ['Uneasy, if I\'m honest. %cause%. It sits with you, a thing like that.'] },
      { criteria: [is('feelingTone', 'down')], lines: ['Been better. %cause%. It\'ll pass; things do.'] },
      { criteria: [is('hungry', true)], lines: ['Hungry, mostly. Everything else can wait for a meal.'] },
      { criteria: [(f) => f.memoryLine !== '' && f.feelingTone === ''], lines: ['Fair enough. Though I keep coming back to this: %memory%'] },
    ],
  },
  {
    key: 'weather', label: 'The sky', base: false,
    aliases: ['weather', 'sky', 'rain', 'snow', 'cold', 'storm', 'clouds'],
    knows: always,
    rules: [
      { criteria: [], lines: ['Sky\'s doing what it likes, as usual. We work under it either way.'] },
      { criteria: [(f) => f.weather === 'rain'], lines: ['Rain. The terraces drink it and the reclaim sings — the streets curse it anyway.'] },
      { criteria: [(f) => f.weather === 'snow'], lines: ['Snow\'s down. Roads slow, tempers short, heat-exchangers earning their keep.'] },
      { criteria: [(f) => f.weather === 'rain', is('job', 'farmer')], lines: ['Rain! Best sound there is. Every drop\'s a credit on next season\'s table.'] },
      { criteria: [(f) => f.weather === 'clear', is('tod', 'night')], lines: ['Clear night. Step past the lamps and look up — half those moving stars are ours. That\'s half the reason anyone settles out here.'] },
    ],
  },
  // ── Dynamic composites (live sim state) ──
  {
    key: 'economy', label: 'The settlement\'s fortunes', base: false,
    aliases: ['economy', 'fortunes', 'business', 'how goes trade', 'the numbers'],
    knows: always,
    rules: [
      { criteria: [], lines: ['Read it in three numbers: rations at %price%, the levy at %tax%, and the canteen noise at dusk. Today all three say "getting by".'] },
      { criteria: [(f) => f.foodPrice > 8], lines: ['Strained. Rations at %price% and the levy still at %tax% — the keeper\'s juggling, and everyone\'s counting.'] },
      { criteria: [(f) => f.foodPrice < 3 && f.taxPct < 6], lines: ['Fat season, honestly. Rations at %price%, levy easy at %tax%, and the fab queue short. Enjoy it; the weather will hear us bragging.'] },
      { criteria: [(f) => f.project !== ''], lines: ['Treasury\'s spending — %project% is going up. Work for idle hands and grumbling for idle mouths.'] },
      { criteria: [(f) => f.atWar], lines: ['A war economy: freight thin, escorts dear, vault manifests suddenly everyone\'s favorite reading.'] },
    ],
  },
  {
    key: 'politics', label: 'The council table', base: false,
    aliases: ['politics', 'the council table', 'who decides', 'power', 'the seats'],
    knows: (f) => f.mayorName !== '' || f.keeperName !== '' || f.guildmasterName !== '',
    minStanding: 15,
    rules: [
      { criteria: [], lines: ['%mayor% holds the keeper\'s chair. The guilds push, the households pull, the charter referees. Same table every settlement.'] },
      { criteria: [(f) => f.guildmasterName !== ''], lines: ['Watch %mayor% and %guildmaster% at council — when THOSE two agree, it\'s already decided. When they don\'t, bring snacks.'] },
      { criteria: [(f) => f.custom !== ''], lines: ['Latest from the table: %custom% The benches approve, mostly, which is as close to consensus as we get.'] },
      { criteria: [(f) => f.atWar, tierAtLeast('familiar')], lines: ['War politics is simple arithmetic: the council that ends it well keeps its seats; the council that ends it badly joins the benches. They know it, too.'] },
    ],
  },
  {
    key: 'events', label: 'Goings-on', base: false,
    aliases: ['events', 'goings-on', 'happenings', 'what happened', 'anything happen'],
    knows: always,
    rules: [
      { criteria: [], lines: ['Quiet stretch. The crier\'s been reduced to reading tariff schedules with feeling.'] },
      { criteria: [(f) => f.townNews !== ''], lines: ['The big one: %news%. The benches have opinions; the benches always have opinions.'] },
      { criteria: [(f) => f.townNews !== '', (f) => f.rumors.length > 0], lines: ['On the record: %news%. Off it — %rumor%'] },
      { criteria: [(f) => f.atWar], lines: ['Everything\'s war news wearing different hats. Musters, freight delays, and the memorial ground\'s new row.'] },
    ],
  },
  {
    key: 'wider', label: 'The wider world', base: true,
    aliases: ['wider world', 'ask around', 'what else', 'this world', 'tell me more', 'the cosmos'],
    knows: always,
    rules: [
      { criteria: [], lines: [] }, // filled at module init with rotating lore hooks
    ],
  },
];

// ─── The lore bank — ~220 discoverable topics expanded from data ────────────
const LORE_ALL: LoreEntry[] = [
  ...LORE_PLACES, ...LORE_SPACE, ...LORE_TECH, ...LORE_NATURE, ...LORE_B, ...LORE_C,
];

function loreTopic(e: LoreEntry): TopicDef {
  const arr = (v: string | string[] | undefined): string[] =>
    v === undefined ? [] : Array.isArray(v) ? v : [v];
  const rules: TopicRule[] = [{ criteria: [], lines: arr(e.s) }];
  if (e.f) rules.push({ criteria: [tierAtLeast('familiar')], lines: arr(e.f) });
  // TWO criteria: bestRule picks by criteria count, and the familiar rule
  // (1 criterion) would tie-and-shadow a 1-criterion neighbor rule forever.
  if (e.n) rules.push({ criteria: [tierAtLeast('familiar'), tierAtLeast('neighbor')], lines: arr(e.n) });
  return {
    key: `lore:${e.k}`, label: e.l, aliases: e.a, base: false,
    knows: (f) => (!e.war || f.atWar) && (!e.civ || f.civDemonym !== ''),
    minStanding: e.gate,
    rules,
  };
}
TOPICS.push(...LORE_ALL.map(loreTopic));

// The 'wider world' hub: rotating lines that NAME lore aliases so the link
// scanner lights them — the discovery faucet for the big bank (typing works
// too; matchTopic knows every alias).
{
  const hub = TOPICS.find((t) => t.key === 'wider')!;
  const lines: string[] = [];
  for (let v = 0; v < 12; v++) {
    const picks: string[] = [];
    for (let j = 0; j < 4; j++) {
      const e = LORE_ALL[(v * 37 + j * 53) % LORE_ALL.length];
      picks.push(e.a[0]);
    }
    lines.push(
      v % 3 === 0
        ? `Plenty out here worth asking after — ${picks[0]}, ${picks[1]}, ${picks[2]}, or ${picks[3]}, for a start.`
        : v % 3 === 1
          ? `Depends what you\'re curious about. People here can talk your ear off about ${picks[0]}, ${picks[1]}, even ${picks[2]}. And don\'t get the benches started on ${picks[3]}.`
          : `Big world, bigger sky. Ask about ${picks[0]} or ${picks[1]} if you\'re practical; ${picks[2]} or ${picks[3]} if you\'re not.`,
    );
  }
  hub.rules[0].lines = lines;
}

const TOPIC_BY_KEY = new Map(TOPICS.map((t) => [t.key, t]));
export function topicByKey(key: string): TopicDef | undefined { return TOPIC_BY_KEY.get(key); }

// ─── Selection + fills ───────────────────────────────────────────────────────

function bestRule(rules: TopicRule[], f: TopicFacts): TopicRule | null {
  let best: TopicRule | null = null;
  let bestN = -1;
  for (const r of rules) {
    let ok = true;
    for (const c of r.criteria) if (!c(f)) { ok = false; break; }
    if (ok && r.criteria.length > bestN) { best = r; bestN = r.criteria.length; }
  }
  return best;
}

const VENUE_WHY: Record<string, string> = {
  civic: 'Settlement business runs through it.', market: 'Credits and news change hands in equal measure.',
  entertainment: 'Where the settlement unwinds after shift.', manufacturing: 'You can hear the fabricators from here.',
  agriculture: 'It keeps every table in town.', faith: 'Quieter than the rest of town, by design.',
  defense: 'The watch keeps it manned day and night.', industry: 'Dust, noise, and half our livelihood.',
  science: 'Charts and measurements — the settlement\'s memory of the land.',
};

export function fillLine(line: string, f: TopicFacts): string {
  const rumor = f.rumors.length > 0 ? f.rumors[0] : (f.townNews || 'nothing much');
  return line
    .replace(/%player%/g, f.playerName || 'stranger')
    .replace(/%job%/g, f.job)
    .replace(/%town%/g, f.town || 'this town')
    .replace(/%venuewhy%/g, VENUE_WHY[f.venueFn] ?? 'It earns its keep.')
    .replace(/%venue%/g, f.venue)
    .replace(/%price%/g, `${f.foodPrice} credit${f.foodPrice === 1 ? '' : 's'}`)
    .replace(/%tax%/g, `${f.taxPct}%`)
    .replace(/%project%/g, f.project || 'something')
    .replace(/%custom%/g, f.custom)
    .replace(/%memory%/g, f.memoryLine)
    .replace(/%cause%/g, f.feelingCause)
    .replace(/%news%/g, f.townNews || 'nothing much')
    .replace(/%rumor%/g, rumor)
    .replace(/%civ%/g, f.civName || 'settlers')
    .replace(/%demonym%/g, f.civDemonym || 'local')
    .replace(/%rival%/g, f.rivalDemonym || 'other')
    .replace(/%foe%/g, f.warFoe || 'the enemy')
    .replace(/%casualties%/g, String(f.warCasualties))
    .replace(/%mayor%/g, f.mayorName || f.keeperName || 'the keeper')
    .replace(/%guildmaster%/g, f.guildmasterName)
    .replace(/%workplace%/g, f.workplace || 'the works')
    .replace(/%family%/g, f.familyLine)
    .replace(/%thought%/g, f.thought || 'nothing much, truth be told')
    .replace(/%diary%/g, f.diaryLine || 'a plain day')
    .replace(/%bond%/g, f.bondLine)
    .replace(/%witness%/g, f.witnessLines?.[0] || f.memoryLine || 'quiet days, mostly')
    .replace(/%why1%/g, f.relWhys?.[0] ? `${f.relWhys[0].label} — ${f.relWhys[0].ago}` : 'we have no history to speak of')
    .replace(/%why2%/g, f.relWhys?.[1] ? `${f.relWhys[1].label} — ${f.relWhys[1].ago}` : '')
    .replace(/%aspiration%/g, f.aspirationLine || 'to see tomorrow arrive on time');
}

/** Answer a topic: most-specific passing rule, line by shuffle-bag, %fills%. */
const DEFLECT: string[] = [
  'That\'s not talk for strangers, friend. Earn this settlement\'s trust and ask me again.',
  'Mm. The town doesn\'t know you well enough for me to get into that. Standing first, stories after.',
  'Ask me that when people here nod at your name. Not before.',
];

export function respond(topicKey: string, f: TopicFacts, bag: number): string {
  const topic = TOPIC_BY_KEY.get(topicKey);
  if (!topic || !topic.knows(f)) return 'Couldn\'t tell you — not my area.';
  // The relationship information-gate: below the topic\'s regard floor the
  // NPC deflects — and the deflection names the cure, so gating reads as
  // gameplay, not a bug.
  if (topic.minStanding !== undefined && f.standing < topic.minStanding) {
    return DEFLECT[Math.abs(bag) % DEFLECT.length];
  }
  // The disclosure ladder (social penetration): personal depths open by TIER.
  if (topic.minTier !== undefined) {
    const order = ['nemesis', 'hostile', 'cold', 'stranger', 'familiar', 'friend', 'confidant'];
    if (order.indexOf(f.relTier ?? 'stranger') < order.indexOf(topic.minTier)) {
      return DEFLECT[Math.abs(bag) % DEFLECT.length];
    }
  }
  const rule = bestRule(topic.rules, f);
  if (!rule) return 'Couldn\'t tell you — not my area.';
  // Rumors rotate through what they actually carry, not just line variants.
  if (topicKey === 'rumors' && f.rumors.length > 1) {
    f = { ...f, rumors: [f.rumors[Math.abs(bag) % f.rumors.length]] };
  }
  return fillLine(rule.lines[Math.abs(bag) % rule.lines.length], f);
}

/** Topics this NPC can list right now (base ∪ discovered, knows-gated). */
export function availableTopics(f: TopicFacts, discovered: ReadonlySet<string>): TopicDef[] {
  return TOPICS.filter((t) => t.knows(f) && (t.base || discovered.has(t.key)));
}

// ─── Greetings — the priority ladder ─────────────────────────────────────────

/** Two topic hooks woven into the greeting so the link scanner lights them. */
function greetHook(f: TopicFacts): string {
  if (f.atWar) return 'Ask about the war if you must, or the latest rumors if you\'d rather pretend.';
  if (f.rumors.length > 0 || f.townNews !== '') return 'If you\'re after the latest rumors, you found the right ears.';
  return 'Ask about my work, or the town — I\'m told I go on about both.';
}

export function composeGreeting(f: TopicFacts, bag: number): string {
  const t = standingTierOf(f.standing);
  const pick = (lines: string[]): string => fillLine(lines[Math.abs(bag) % lines.length], f);
  let head: string;
  if (f.relTier === 'nemesis') {
    head = pick(['YOU. %why1% — and here you stand, bold as brass. Say it and go.', 'I swore against you, %player%. %why1%. The whole town knows what you are.']);
  } else if ((f.disposition?.fear ?? 0) >= 25) {
    // Fear outranks warmth: gifts after a shooting buy a wary distance, not a welcome.
    head = pick(['Stay where you are, %player%. %why1% — I have not forgotten.', 'Keep your distance. %why1%. Say what you came to say from there, %player%.']);
  } else if (f.rivalFlag && f.relNet > -15) {
    head = pick(['Well, well — my favorite rival. Keeping up today, %player%?', 'You again. The hunt goes to the quick, %player% — try to be quick.']);
  } else if (f.relTier === 'confidant') {
    head = pick(['%player%! Sit, sit — no ceremony between us.', 'There you are. I kept back a story only you get to hear, %player%.']);
  } else if (f.relTier === 'friend' && f.talkStreak >= 3) {
    head = pick(['Every day this week, %player% — I notice these things, and it counts.', 'My daily visitor. The day is better for it, %player%.']);
  } else if (f.playerOpinion <= -20) {
    head = pick(['You. I know what you did to our cargo ship, %player%. Say your piece and go.', 'The sky pirate walks into MY town. Bold. Talk fast, %player%.']);
  } else if (f.atWar && f.warWeary) {
    head = pick(['Unless you bring an end to the war with %foe%, keep it brief.', 'A war on and you want conversation. Fine — talk.']);
  } else if (f.feelingTone === 'angry') {
    head = pick(['Not the best time, %player%. %cause%, if you must know.', 'Careful today. %cause% — I\'m in no mood.']);
  } else if (f.feelingTone === 'down') {
    head = pick(['Oh. %player%. Forgive me — %cause%. What is it?', 'I\'m poor company today. %cause%. Still — speak.']);
  } else if (t === 'wary') {
    head = pick(['I know who you are, %player%. State your business and keep your hands where the watch can see them.', 'We don\'t know you kindly here, %player%. Talk, then walk.']);
  } else if (t === 'neighbor') {
    head = pick(['%player%! Always a good day when a friend of %town% comes by.', 'Well met, neighbor. What can I do for you, %player%?']);
  } else if (f.chattedBefore) {
    head = pick(['Back again, %player%? Good. I remember our last talk.', 'You again, %player% — I mean that kindly. What now?']);
  } else {
    // Activity + place + time — the "aware of what they're doing" layer.
    const doing: Record<string, string[]> = {
      working: ['Caught me mid-shift — %job% work doesn\'t pause for talk, but I can manage both.'],
      building: ['Mind the dust — we\'re building here. What do you need?'],
      trading: ['Make it quick or make it profitable — I\'m mid-trade.'],
      to_market: ['Walk with me — I\'m hauling this lot to the market.'],
      to_work: ['On my way to work, but go on.'],
      social: ['You\'ve caught me idling with the neighbors, so you\'re in luck.'],
      to_social: ['I was headed for better company, but you\'ll do.'],
      chatting: ['One conversation at a time, friend — all right, yours.'],
      resting: ['I was resting, but the feet can wait. What is it?'],
      patrolling: ['Keep to the lamps after dark, %player%. Now — what?'],
      wandering: ['Just stretching my legs about %town%. Company\'s welcome.'],
    };
    const stateLines = doing[f.state];
    if (stateLines && f.venue !== '') {
      head = pick(stateLines.map((l) => `${l.slice(0, -1)} — here by ${f.venue}.`));
    } else if (stateLines) {
      head = pick(stateLines);
    } else if (f.tod === 'night') {
      head = pick(['Late to be out, %player%. Quick, then.', 'Quiet night in %town%. What brings you?']);
    } else {
      head = pick(['Well met, %player%.', 'Hey there. Need something?', 'A moment for a stranger? All right.']);
    }
  }
  return `${head} ${greetHook(f)}`;
}

// ─── Hyperlinks — Morrowind's blue keywords ──────────────────────────────────

export interface TextSegment { kind: 'text' | 'link'; text: string; topicKey?: string }

/**
 * Scan rendered dialogue for other known topics' aliases → clickable segments.
 * Longest alias first, whole-word, one link per topic, ≤4 links per response.
 */
/**
 * Aliases that MATCH typed questions but are never LINKED in a versona's speech (2026-09-20). An alias does two
 * jobs; in the versona's own first person "me" is THEM, not the player topic ("they'll bury me in Aethon" linked
 * "me" to Yourself), and "people" / "place" are just words. The dialogue paths speak in the first person a lot.
 */
const LINK_STOP = new Set(['me', 'myself', 'people', 'place']);

export function linkify(text: string, f: TopicFacts, excludeTopic?: string): TextSegment[] {
  interface Hit { start: number; end: number; key: string }
  const hits: Hit[] = [];
  const lower = text.toLowerCase();
  const isWord = (c: string | undefined): boolean => c !== undefined && /[a-z0-9]/i.test(c);
  for (const topic of TOPICS) {
    if (topic.key === excludeTopic || !topic.knows(f)) continue;
    let found: Hit | null = null;
    for (const alias of [...topic.aliases].sort((a, b) => b.length - a.length)) {
      const a = alias.toLowerCase();
      if (LINK_STOP.has(a)) continue;
      let from = 0;
      while (found === null) {
        const i = lower.indexOf(a, from);
        if (i < 0) break;
        if (!isWord(lower[i - 1]) && !isWord(lower[i + a.length])) {
          const overlaps = hits.some((h) => i < h.end && i + a.length > h.start);
          if (!overlaps) { found = { start: i, end: i + a.length, key: topic.key }; break; }
        }
        from = i + 1;
      }
      if (found) break;
    }
    if (found) hits.push(found);
    if (hits.length >= 4) break;
  }
  hits.sort((a, b) => a.start - b.start);
  const out: TextSegment[] = [];
  let cursor = 0;
  for (const h of hits) {
    if (h.start > cursor) out.push({ kind: 'text', text: text.slice(cursor, h.start) });
    out.push({ kind: 'link', text: text.slice(h.start, h.end), topicKey: h.key });
    cursor = h.end;
  }
  if (cursor < text.length) out.push({ kind: 'text', text: text.slice(cursor) });
  return out.length > 0 ? out : [{ kind: 'text', text }];
}

// ─── Free-text NLP — typed questions land on topics ──────────────────────────

const STOP = new Set([
  'a', 'an', 'the', 'me', 'my', 'i', 'im', 'you', 'your', 'yours', 'we', 'us',
  'tell', 'of', 'to', 'is', 'are', 'was', 'were', 'be', 'been', 'it', 'its',
  'do', 'does', 'did', 'can', 'could', 'would', 'will', 'have', 'has', 'had',
  'in', 'on', 'at', 'for', 'and', 'or', 'with', 'this', 'that', 'these',
  'any', 'some', 'know', 'about', 'so', 'hey', 'hi', 'hello', 'please', 'going',
  'whats', 'what', 'who', 'whos', 'why', 'when', 'there', 'got', 'get', 'give',
]);

/** Light stemmer: plural/participle suffixes off tokens (rumors→rumor). */
function stem(w: string): string {
  if (w.length > 5 && w.endsWith('ing')) return w.slice(0, -3);
  if (w.length > 4 && w.endsWith('ed')) return w.slice(0, -2);
  if (w.length > 3 && w.endsWith('es')) return w.slice(0, -2);
  if (w.length > 3 && w.endsWith('s')) return w.slice(0, -1);
  return w;
}

function tokenize(s: string): string[] {
  return s.toLowerCase().replace(/[^a-z0-9\s]/g, ' ').split(/\s+/)
    .filter((w) => w.length > 1 && !STOP.has(w)).map(stem);
}

/** Levenshtein distance capped at 3 (early-exit band) — typo tolerance. */
function lev(a: string, b: string): number {
  if (Math.abs(a.length - b.length) > 2) return 3;
  const prev = new Array<number>(b.length + 1);
  for (let j = 0; j <= b.length; j++) prev[j] = j;
  for (let i = 1; i <= a.length; i++) {
    let diag = prev[0];
    prev[0] = i;
    let rowMin = prev[0];
    for (let j = 1; j <= b.length; j++) {
      const tmp = prev[j];
      prev[j] = Math.min(prev[j] + 1, prev[j - 1] + 1, diag + (a[i - 1] === b[j - 1] ? 0 : 1));
      diag = tmp;
      if (prev[j] < rowMin) rowMin = prev[j];
    }
    if (rowMin > 2) return 3;
  }
  return Math.min(prev[b.length], 3);
}

function tokensMatch(input: string, alias: string): boolean {
  if (input === alias) return true;
  return input.length >= 5 && alias.length >= 5 && lev(input, alias) <= (input.length >= 7 ? 2 : 1);
}

export interface TopicMatch { key: string; score: number }

/**
 * Match typed input to a topic the NPC knows. Phrase hits score highest;
 * token hits (stemmed, typo-tolerant) accumulate. null = no confident match
 * (→ escalate to the LLM, or redirect with nearTopics()).
 */
export function matchTopic(input: string, f: TopicFacts): TopicMatch | null {
  const norm = ` ${input.toLowerCase().replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim()} `;
  const toks = tokenize(input);
  if (norm.trim() === '' ) return null;
  let best: TopicMatch | null = null;
  for (const topic of TOPICS) {
    if (!topic.knows(f)) continue;
    let score = 0;
    for (const alias of topic.aliases) {
      if (alias.includes(' ')) {
        if (norm.includes(` ${alias} `)) score = Math.max(score, 3);
        continue;
      }
      const a = stem(alias.toLowerCase());
      for (const t of toks) if (tokensMatch(t, a)) { score = Math.max(score, t === a ? 2 : 1.5); break; }
    }
    if (score > 0 && (best === null || score > best.score)) best = { key: topic.key, score };
  }
  return best !== null && best.score >= 1.5 ? best : null;
}

/** Best two topic labels for the polite no-match redirect line. */
export function nearTopics(f: TopicFacts, discovered: ReadonlySet<string>): string[] {
  const avail = availableTopics(f, discovered);
  const prio = ['rumors', 'work', 'town'];
  const picks = prio.filter((k) => avail.some((t) => t.key === k)).slice(0, 2);
  return picks.map((k) => TOPIC_BY_KEY.get(k)!.label.toLowerCase());
}
